import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { Pencil, Trash2 } from 'lucide-react';
import type { ApiError } from '@pms/shared';
import { PERMISSIONS } from '@pms/shared';
import {
  subscriptionService,
  type SubscriptionCategory,
  type SubscriptionCurrency,
  type SubscriptionStatus,
  type SubscriptionBillingCycle,
  type SubscriptionAuditLog,
} from '@/services/subscription.service';
import { PermissionGate } from '@/components/guards/PermissionGate';
import { Tabs } from '@/components/ui/tabs';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { CategoryFormDialog } from './components/CategoryFormDialog';
import { CurrencyFormDialog } from './components/CurrencyFormDialog';
import { StatusFormDialog } from './components/StatusFormDialog';
import { BillingCycleFormDialog } from './components/BillingCycleFormDialog';
import { format } from 'date-fns';

function errMsg(e: unknown): string {
  return (e as AxiosError<ApiError>).response?.data?.error?.message ?? 'Something went wrong';
}

const TABS = [
  { key: 'categories', label: 'Categories' },
  { key: 'currencies', label: 'Currencies' },
  { key: 'statuses', label: 'Statuses' },
  { key: 'billing-cycles', label: 'Billing Cycles' },
  { key: 'audit-logs', label: 'Audit Logs' },
];

// ─── Categories Tab ───────────────────────────────────────────────────────────

function CategoriesTab({ data, loading }: { data: SubscriptionCategory[]; loading: boolean }) {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<{ mode: 'create' | 'edit'; item?: SubscriptionCategory } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const deleteMut = useMutation({
    mutationFn: (id: string) => subscriptionService.deleteCategory(id),
    onSuccess: () => { toast.success('Category deleted'); void queryClient.invalidateQueries({ queryKey: ['subscriptions', 'categories'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const columns: Column<SubscriptionCategory>[] = [
    {
      key: 'color', header: 'Color', className: 'w-12',
      cell: (r) => <span className="h-4 w-4 rounded-full block" style={{ backgroundColor: r.color }} />,
    },
    { key: 'name', header: 'Name', cell: (r) => <span className="font-medium">{r.name}</span> },
    {
      key: 'description', header: 'Description',
      cell: (r) => r.description ? <span className="text-sm text-muted-foreground truncate max-w-xs block">{r.description}</span> : <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'status', header: 'Status',
      cell: (r) => <Badge variant={r.is_active ? 'success' : 'secondary'}>{r.is_active ? 'Active' : 'Inactive'}</Badge>,
    },
    {
      key: 'actions', header: <span className="sr-only">Actions</span>, className: 'text-right',
      cell: (r) => (
        <div className="flex items-center justify-end gap-0.5">
          <PermissionGate permission={PERMISSIONS.SUBSCRIPTIONS_MASTER}>
            <button type="button" onClick={() => setDialog({ mode: 'edit', item: r })} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground">
              <Pencil className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => setDeleteTarget({ id: r.id, name: r.name })} className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
              <Trash2 className="h-4 w-4" />
            </button>
          </PermissionGate>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="flex justify-end mb-4">
        <PermissionGate permission={PERMISSIONS.SUBSCRIPTIONS_MASTER}>
          <Button onClick={() => setDialog({ mode: 'create' })}>+ Add Category</Button>
        </PermissionGate>
      </div>
      <DataTable columns={columns} rows={data} rowKey={(r) => r.id} loading={loading} empty="No categories yet." />
      {dialog && (
        <CategoryFormDialog open mode={dialog.mode} category={dialog.item} onClose={() => setDialog(null)} onSuccess={() => setDialog(null)} />
      )}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>Delete &quot;{deleteTarget?.name}&quot;? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { if (deleteTarget) { deleteMut.mutate(deleteTarget.id); setDeleteTarget(null); } }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── Currencies Tab ───────────────────────────────────────────────────────────

function CurrenciesTab({ data, loading }: { data: SubscriptionCurrency[]; loading: boolean }) {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<{ mode: 'create' | 'edit'; item?: SubscriptionCurrency } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const deleteMut = useMutation({
    mutationFn: (id: string) => subscriptionService.deleteCurrency(id),
    onSuccess: () => { toast.success('Currency deleted'); void queryClient.invalidateQueries({ queryKey: ['subscriptions', 'currencies'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const columns: Column<SubscriptionCurrency>[] = [
    { key: 'code', header: 'Code', cell: (r) => <code className="font-mono text-xs font-semibold">{r.code}</code> },
    { key: 'symbol', header: 'Symbol', cell: (r) => <span className="font-medium">{r.symbol}</span> },
    { key: 'name', header: 'Name', cell: (r) => r.name },
    {
      key: 'default', header: 'Default',
      cell: (r) => r.is_default ? <Badge variant="success">Default</Badge> : <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'status', header: 'Status',
      cell: (r) => <Badge variant={r.is_active ? 'success' : 'secondary'}>{r.is_active ? 'Active' : 'Inactive'}</Badge>,
    },
    {
      key: 'actions', header: <span className="sr-only">Actions</span>, className: 'text-right',
      cell: (r) => (
        <div className="flex items-center justify-end gap-0.5">
          <PermissionGate permission={PERMISSIONS.SUBSCRIPTIONS_MASTER}>
            <button type="button" onClick={() => setDialog({ mode: 'edit', item: r })} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground">
              <Pencil className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => setDeleteTarget({ id: r.id, name: r.code })} className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive" disabled={r.is_default} title={r.is_default ? 'Cannot delete the default currency' : undefined}>
              <Trash2 className="h-4 w-4" />
            </button>
          </PermissionGate>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="flex justify-end mb-4">
        <PermissionGate permission={PERMISSIONS.SUBSCRIPTIONS_MASTER}>
          <Button onClick={() => setDialog({ mode: 'create' })}>+ Add Currency</Button>
        </PermissionGate>
      </div>
      <DataTable columns={columns} rows={data} rowKey={(r) => r.id} loading={loading} empty="No currencies yet." />
      {dialog && (
        <CurrencyFormDialog open mode={dialog.mode} currency={dialog.item} onClose={() => setDialog(null)} onSuccess={() => setDialog(null)} />
      )}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Currency</AlertDialogTitle>
            <AlertDialogDescription>Delete &quot;{deleteTarget?.name}&quot;? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { if (deleteTarget) { deleteMut.mutate(deleteTarget.id); setDeleteTarget(null); } }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── Statuses Tab ─────────────────────────────────────────────────────────────

function StatusesTab({ data, loading }: { data: SubscriptionStatus[]; loading: boolean }) {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<{ mode: 'create' | 'edit'; item?: SubscriptionStatus } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const deleteMut = useMutation({
    mutationFn: (id: string) => subscriptionService.deleteStatus(id),
    onSuccess: () => { toast.success('Status deleted'); void queryClient.invalidateQueries({ queryKey: ['subscriptions', 'statuses'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const columns: Column<SubscriptionStatus>[] = [
    {
      key: 'color', header: 'Color', className: 'w-12',
      cell: (r) => (
        <div className="flex items-center gap-2">
          <span className="h-4 w-4 rounded-full border border-border/40" style={{ backgroundColor: r.color }} />
          <code className="text-xs">{r.color}</code>
        </div>
      ),
    },
    { key: 'name', header: 'Name', cell: (r) => <span className="font-medium">{r.name}</span> },
    { key: 'order', header: 'Order', cell: (r) => r.display_order },
    {
      key: 'default', header: 'Default',
      cell: (r) => r.is_default ? <Badge variant="success">Default</Badge> : <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'actions', header: <span className="sr-only">Actions</span>, className: 'text-right',
      cell: (r) => (
        <div className="flex items-center justify-end gap-0.5">
          <PermissionGate permission={PERMISSIONS.SUBSCRIPTIONS_MASTER}>
            <button type="button" onClick={() => setDialog({ mode: 'edit', item: r })} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground">
              <Pencil className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => setDeleteTarget({ id: r.id, name: r.name })} disabled={r.is_default} className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-40 disabled:pointer-events-none" title={r.is_default ? 'Cannot delete default status' : undefined}>
              <Trash2 className="h-4 w-4" />
            </button>
          </PermissionGate>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="flex justify-end mb-4">
        <PermissionGate permission={PERMISSIONS.SUBSCRIPTIONS_MASTER}>
          <Button onClick={() => setDialog({ mode: 'create' })}>+ Add Status</Button>
        </PermissionGate>
      </div>
      <DataTable columns={columns} rows={data} rowKey={(r) => r.id} loading={loading} empty="No statuses yet." />
      {dialog && (
        <StatusFormDialog open mode={dialog.mode} status={dialog.item} onClose={() => setDialog(null)} onSuccess={() => setDialog(null)} />
      )}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Status</AlertDialogTitle>
            <AlertDialogDescription>Delete &quot;{deleteTarget?.name}&quot;? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { if (deleteTarget) { deleteMut.mutate(deleteTarget.id); setDeleteTarget(null); } }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── Billing Cycles Tab ───────────────────────────────────────────────────────

function BillingCyclesTab({ data, loading }: { data: SubscriptionBillingCycle[]; loading: boolean }) {
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<{ mode: 'create' | 'edit'; item?: SubscriptionBillingCycle } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);

  const deleteMut = useMutation({
    mutationFn: (id: string) => subscriptionService.deleteBillingCycle(id),
    onSuccess: () => { toast.success('Billing cycle deleted'); void queryClient.invalidateQueries({ queryKey: ['subscriptions', 'billing-cycles'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const columns: Column<SubscriptionBillingCycle>[] = [
    { key: 'name', header: 'Name', cell: (r) => <span className="font-medium">{r.name}</span> },
    { key: 'code', header: 'Code', cell: (r) => <code className="font-mono text-xs">{r.code}</code> },
    {
      key: 'interval', header: 'Interval',
      cell: (r) => {
        if (r.interval_months) return `${r.interval_months} month${r.interval_months !== 1 ? 's' : ''}`;
        if (r.interval_days) return `${r.interval_days} days`;
        return <span className="text-muted-foreground">—</span>;
      },
    },
    {
      key: 'recurring', header: 'Recurring',
      cell: (r) => <Badge variant={r.is_recurring ? 'success' : 'secondary'}>{r.is_recurring ? 'Yes' : 'One-time'}</Badge>,
    },
    {
      key: 'actions', header: <span className="sr-only">Actions</span>, className: 'text-right',
      cell: (r) => (
        <div className="flex items-center justify-end gap-0.5">
          <PermissionGate permission={PERMISSIONS.SUBSCRIPTIONS_MASTER}>
            <button type="button" onClick={() => setDialog({ mode: 'edit', item: r })} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground">
              <Pencil className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => setDeleteTarget({ id: r.id, name: r.name })} className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive">
              <Trash2 className="h-4 w-4" />
            </button>
          </PermissionGate>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="flex justify-end mb-4">
        <PermissionGate permission={PERMISSIONS.SUBSCRIPTIONS_MASTER}>
          <Button onClick={() => setDialog({ mode: 'create' })}>+ Add Billing Cycle</Button>
        </PermissionGate>
      </div>
      <DataTable columns={columns} rows={data} rowKey={(r) => r.id} loading={loading} empty="No billing cycles yet." />
      {dialog && (
        <BillingCycleFormDialog open mode={dialog.mode} cycle={dialog.item} onClose={() => setDialog(null)} onSuccess={() => setDialog(null)} />
      )}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Billing Cycle</AlertDialogTitle>
            <AlertDialogDescription>Delete &quot;{deleteTarget?.name}&quot;? This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => { if (deleteTarget) { deleteMut.mutate(deleteTarget.id); setDeleteTarget(null); } }}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ─── Audit Logs Tab ───────────────────────────────────────────────────────────

const ACTION_COLORS: Record<string, string> = {
  created: 'bg-emerald-100 text-emerald-700',
  updated: 'bg-teal-100 text-teal-700',
  renewed: 'bg-violet-100 text-violet-700',
  cancelled: 'bg-red-100 text-red-700',
  paused: 'bg-amber-100 text-amber-700',
  resumed: 'bg-teal-100 text-teal-700',
  expired: 'bg-slate-100 text-slate-700',
  deleted: 'bg-red-100 text-red-700',
};

function AuditLogsTab() {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['subscriptions', 'audit-logs', page],
    queryFn: () => subscriptionService.getAuditLogs({ page, limit: 25 }),
  });

  const columns: Column<SubscriptionAuditLog>[] = [
    {
      key: 'action', header: 'Action',
      cell: (r) => (
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${ACTION_COLORS[r.action] ?? 'bg-muted text-muted-foreground'}`}>
          {r.action}
        </span>
      ),
    },
    {
      key: 'subscription', header: 'Subscription',
      cell: (r) => r.subscription_name ?? <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'by', header: 'By',
      cell: (r) => r.actor_first ? `${r.actor_first} ${r.actor_last ?? ''}`.trim() : <span className="text-muted-foreground">System</span>,
    },
    {
      key: 'date', header: 'Date',
      cell: (r) => <span className="text-muted-foreground text-xs">{format(new Date(r.created_at), 'dd MMM yyyy HH:mm')}</span>,
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={data?.data ?? []}
      rowKey={(r) => r.id}
      loading={isLoading}
      error={isError}
      onRetry={() => void refetch()}
      empty="No audit logs yet."
      pagination={data ? {
        page: data.pagination.page,
        totalPages: data.pagination.totalPages,
        total: data.pagination.total,
        onPageChange: setPage,
      } : undefined}
    />
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function SubscriptionMasterPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') ?? 'categories';
  const setTab = (key: string) => setSearchParams({ tab: key });

  const categoriesQ = useQuery({ queryKey: ['subscriptions', 'categories'], queryFn: () => subscriptionService.listCategories() });
  const currenciesQ = useQuery({ queryKey: ['subscriptions', 'currencies'], queryFn: () => subscriptionService.listCurrencies() });
  const statusesQ = useQuery({ queryKey: ['subscriptions', 'statuses'], queryFn: () => subscriptionService.listStatuses() });
  const cyclesQ = useQuery({ queryKey: ['subscriptions', 'billing-cycles'], queryFn: () => subscriptionService.listBillingCycles() });

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-semibold text-foreground">Subscription Master</h1>
      <Tabs tabs={TABS} active={activeTab} onChange={setTab} />
      <div className="mt-4">
        {activeTab === 'categories' && <CategoriesTab data={categoriesQ.data ?? []} loading={categoriesQ.isLoading} />}
        {activeTab === 'currencies' && <CurrenciesTab data={currenciesQ.data ?? []} loading={currenciesQ.isLoading} />}
        {activeTab === 'statuses' && <StatusesTab data={statusesQ.data ?? []} loading={statusesQ.isLoading} />}
        {activeTab === 'billing-cycles' && <BillingCyclesTab data={cyclesQ.data ?? []} loading={cyclesQ.isLoading} />}
        {activeTab === 'audit-logs' && <AuditLogsTab />}
      </div>
    </div>
  );
}
